import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined';
import {
  cancelStatisticsPendingRequest,
  getStatisticsPendingRequestContext,
} from '../api/training';

function StatisticsPendingEntryCancelDialog({ requestId, open, onClose, onCancelled }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [context, setContext] = useState(null);
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const loadContext = async ({ preserveError = false } = {}) => {
    setLoading(true);
    if (!preserveError) setError('');
    try {
      const { data } = await getStatisticsPendingRequestContext(requestId);
      setContext(data);
    } catch (loadError) {
      setContext(null);
      setError(loadError.response?.data?.error || 'Die offene Eintragsanfrage konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !requestId) return;
    setReason('');
    setConfirmed(false);
    loadContext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, requestId]);

  const handleCancelRequest = async () => {
    setSubmitting(true);
    setError('');
    try {
      await cancelStatisticsPendingRequest(requestId, {
        requestRevision: context.requestRevision,
        reason: reason.trim(),
        confirmCancel: confirmed,
      });
      onCancelled();
    } catch (cancelError) {
      const message = cancelError.response?.data?.error || 'Die Eintragsanfrage konnte nicht zurückgezogen werden.';
      setError(message);
      if (cancelError.response?.status === 409) {
        setConfirmed(false);
        await loadContext({ preserveError: true });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm" fullScreen={fullScreen}>
      <DialogTitle>Eintragsanfrage zurückziehen</DialogTitle>
      <DialogContent dividers>
        {loading && <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 180 }}><CircularProgress /></Box>}
        {!loading && !context && error && <Alert severity="error">{error}</Alert>}
        {!loading && context && (
          <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}
            <Alert severity="warning">
              Die noch nicht genehmigte Anfrage wird zurückgezogen. Es wird kein bestehender Logbucheintrag gelöscht.
            </Alert>
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{context.target?.fullName}</Typography>
              <Typography variant="body2" color="text.secondary">
                {`${context.entry?.date || '-'} · ${context.entry?.entryType || '-'} · Antragsteller: ${context.requester?.fullName || '-'}`}
              </Typography>
            </Box>
            <TextField required fullWidth multiline minRows={2} label="Begründung" value={reason} onChange={(event) => setReason(event.target.value)} />
            <FormControlLabel
              control={<Checkbox checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} />}
              label="Ich bestätige, dass ich diese offene Eintragsanfrage zurückziehen möchte."
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={submitting}>Abbrechen</Button>
        <Button
          color="warning"
          variant="contained"
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <UndoOutlinedIcon />}
          onClick={handleCancelRequest}
          disabled={loading || submitting || !context || !reason.trim() || !confirmed}
        >
          Anfrage zurückziehen
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default StatisticsPendingEntryCancelDialog;
